/** Helpers for "is this team row the logged-in user?" (ticket 14428). */

export function getLoggedInClerkId(clerkUser) {
  return clerkUser?.id ?? null;
}

export function normalizeSubRoles(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === "string") return raw.trim() ? [raw.trim()] : [];
  return [];
}

export function hasVendorAdminSubRole(rawSubRoles) {
  return normalizeSubRoles(rawSubRoles).includes("Vendor Admin");
}

export function getVendorTeamMemberClerkId(row) {
  if (!row) return null;
  return (
    row.provider_user_clerk_id ||
    row.clerk_id ||
    row.user_clerk_id ||
    null
  );
}

export function getSponsorTeamMemberClerkId(row) {
  if (!row) return null;
  return (
    row.sponser_clerk_id ||
    row.sponsor_clerk_id ||
    row.clerk_id ||
    row.user_clerk_id ||
    row.id ||
    null
  );
}

export function isLoggedInVendorTeamMember(row, clerkUserId) {
  const rowClerk = getVendorTeamMemberClerkId(row);
  if (!clerkUserId || !rowClerk) return false;
  return String(rowClerk) === String(clerkUserId);
}

export function isLoggedInSponsorTeamMember(row, clerkUserId) {
  const rowClerk = getSponsorTeamMemberClerkId(row);
  if (!clerkUserId || !rowClerk) return false;
  return String(rowClerk) === String(clerkUserId);
}

export function isLoggedInTeamMember(row, clerkUserId, { isVendorView } = {}) {
  return isVendorView
    ? isLoggedInVendorTeamMember(row, clerkUserId)
    : isLoggedInSponsorTeamMember(row, clerkUserId);
}

export function teamMemberRowClassName(
  item,
  { isMe, inactiveClass = "opacity-50", meClass = "bg-indigo-50" } = {}
) {
  const classes = [];
  if (item?.is_active === false) classes.push(inactiveClass);
  if (isMe) classes.push(meClass);
  return classes.join(" ");
}

/** Self-profile route when managing the logged-in user's own team row. */
export function getSelfProfilePath({
  member,
  clerkUserId,
  isVendorView,
  isSponsorView,
  currentUserType,
}) {
  if (isVendorView && isLoggedInVendorTeamMember(member, clerkUserId)) {
    if (currentUserType === "vendor") return "/Provider/Users/Me";
    if (currentUserType === "admin") return "/Backend/Users/Me";
  }
  if (isSponsorView && isLoggedInSponsorTeamMember(member, clerkUserId)) {
    if (currentUserType === "sponsor") return "/Sponsor/Users/Me";
    if (currentUserType === "admin") return "/Backend/Users/Me";
  }
  return null;
}

function encodeOrgId(value) {
  return value != null && value !== "" ? btoa(String(value)) : "";
}

function queryString(params) {
  return new URLSearchParams(
    Object.fromEntries(Object.entries(params).map(([k, v]) => [k, v ?? ""]))
  ).toString();
}

/** Manage URL for a team-grid row, or the self-profile path when the row is the current user. */
export function resolveTeamMemberManagePath({
  member,
  clerkUserId,
  isVendorView,
  isSponsorView,
  currentUserType,
  sponsorData,
  providerData,
}) {
  const selfPath = getSelfProfilePath({
    member,
    clerkUserId,
    isVendorView,
    isSponsorView,
    currentUserType,
  });
  if (selfPath) return selfPath;

  const sponsorOrgId = encodeOrgId(sponsorData?.hypercomply_org_id);
  const providerOrgId = encodeOrgId(providerData?.hc_org_id);

  if (currentUserType === "vendor") {
    return `/Provider/addTeamMember?${queryString({
      userId: member?.provider_user_id,
      providerName: member?.provider_name,
      clerkId: member?.provider_user_clerk_id,
      orgId: providerOrgId,
    })}`;
  }
  if (currentUserType === "sponsor") {
    return `/Sponsor/AddTeamMember?${queryString({
      userId: member?.id,
      id: member?.sponsor_id,
      name: sponsorData?.sponsor_name,
      orgId: sponsorOrgId,
    })}`;
  }
  if (isVendorView) {
    return `/Backend/Users/AddProviderUser?${queryString({
      userId: member?.provider_user_id,
      providerName: member?.provider_name,
      clerkId: member?.provider_user_clerk_id,
      providerId: providerData?.provider_id,
      orgId: providerOrgId,
    })}`;
  }
  if (isSponsorView) {
    return `/Backend/Users/Sponsors/AddUser?${queryString({
      sponsorName: sponsorData?.sponsor_name,
      sponsorId: member?.sponsor_id,
      userId: member?.id,
    })}`;
  }
  return null;
}
